/**
 * UI-8f Task 6 — CloseDialog.
 *
 * Modal dialog triggered from IncidentDetailPage when a RESOLVED incident
 * is moved to CLOSED. The backend requires a non-empty `lessonsLearned`
 * string when closing from RESOLVED, so Submit stays disabled until the
 * operator types something.
 *
 * Sibling: ResolveDialog (same shape, responseActions). Closing directly
 * from OPEN (the "False alarm" button) doesn't require lessonsLearned and
 * uses no dialog at all — the page calls transition({ to: 'CLOSED' })
 * inline.
 */
import { useEffect, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'

export interface CloseDialogProps {
  open: boolean
  onClose: () => void
  /** Called with the trimmed lessonsLearned text. */
  onSubmit: (text: string) => Promise<void>
}

export function CloseDialog({
  open,
  onClose,
  onSubmit,
}: CloseDialogProps): JSX.Element {
  const [lessonsLearned, setLessonsLearned] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setLessonsLearned('')
      setSubmitting(false)
    }
  }, [open])

  const trimmed = lessonsLearned.trim()
  const valid = trimmed.length > 0

  async function handleSubmit(): Promise<void> {
    if (!valid) return
    setSubmitting(true)
    try {
      await onSubmit(trimmed)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      header="Close Incident"
      visible={open}
      onHide={onClose}
      style={{ width: '32rem' }}
    >
      <div className="flex flex-col gap-3">
        <label
          htmlFor="close-lessons-learned"
          className="text-sm font-medium"
        >
          Lessons Learned <span className="text-red-600">*</span>
        </label>
        <InputTextarea
          id="close-lessons-learned"
          value={lessonsLearned}
          onChange={(e) => {
            setLessonsLearned(e.target.value)
          }}
          rows={6}
          maxLength={5000}
          placeholder="What did we learn? How will we prevent recurrence?"
        />
        <div className="flex justify-end gap-2">
          <Button
            label="Cancel"
            text
            severity="secondary"
            onClick={onClose}
            disabled={submitting}
          />
          <Button
            label="Close"
            disabled={!valid || submitting}
            loading={submitting}
            onClick={() => {
              void handleSubmit()
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}

export default CloseDialog
